import { TradeToEarnCampaign, TradeToEarnCampaignEpoch } from "@cede/types";

export const getTradingCampaignDescription = (
  campaignMetadata: TradeToEarnCampaign,
  epoch?: TradeToEarnCampaignEpoch,
) => {
  // check if the description is defined in the epoch group
  const relatedEpochGroup = campaignMetadata.epochGroups.find((epochGroup) => epochGroup.id === epoch?.epochGroupId);

  if (relatedEpochGroup && relatedEpochGroup.description) {
    return relatedEpochGroup.description;
  }

  // if the description is not defined in the epoch group, check if it's defined in the campaign descriptions (backward compatibility)
  // TODO: remove this once we have fully migrated the trade-to-earn module
  const description = campaignMetadata?.descriptions?.find((description) => {
    let isValid = true;
    if (description.rule && (!epoch || description.rule !== epoch.rule.rule)) {
      isValid = false;
    }

    if (
      description.conditionalRenderingFromDate &&
      (!epoch || epoch.start < description.conditionalRenderingFromDate)
    ) {
      isValid = false;
    }
    return isValid;
  });
  return description?.content || "";
};

export const getCurrentEpoch = (campaign: TradeToEarnCampaign): TradeToEarnCampaignEpoch | undefined => {
  const now = new Date();
  return campaign.epochs.find((epoch) => {
    const { start, end } = epoch;
    return start && end && now >= start && now < end;
  });
};

export const getCurrentEpochIndex = (epochs: TradeToEarnCampaignEpoch[]) => {
  const now = new Date();
  return epochs.findIndex((epoch) => {
    const { start, end } = epoch;
    return start && end && now >= start && now < end;
  });
};

export const getNextEpoch = <T extends TradeToEarnCampaign>(campaign: T) => {
  const currentEpoch = getCurrentEpoch(campaign);
  if (currentEpoch) {
    return campaign.epochs.find((epoch) => {
      const { start, end } = epoch;
      return start && end && start >= currentEpoch.end;
    });
  }
  return campaign.epochs.find((epoch) => epoch.start && epoch.start >= new Date());
};

export const getLastEndedActiveEpoch = (campaign: TradeToEarnCampaign): TradeToEarnCampaignEpoch | null => {
  let lastEndedEpoch: TradeToEarnCampaignEpoch | null = null;
  campaign.epochs.forEach((epoch) => {
    if (epoch.end && new Date() >= epoch.end && !epoch.isFullyPaused) {
      lastEndedEpoch = epoch;
    }
  });
  return lastEndedEpoch;
};

export const getEpochDurationInMs = (epoch: TradeToEarnCampaignEpoch) => {
  let { start, end } = epoch;
  // @todo: remove this once the campaigns are mapped each time we get them from the database
  if (typeof start === "string") {
    start = new Date(start);
  }
  if (typeof end === "string") {
    end = new Date(end);
  }
  return end.getTime() - start.getTime();
};

/**
 * @remarks
 * Returns active epochs for a campaign where trading is still permitted.
 * Active epochs include:
 * - Currently running epochs
 * - Partially paused epochs (paused after start)
 * Excludes:
 * - Fully paused epochs (paused from start)
 *
 * @param campaign - The campaign to get the active epochs from
 */
export const getActiveEpochs = (campaign: TradeToEarnCampaign | null | undefined) => {
  return campaign?.epochs?.filter((epoch) => !epoch.isFullyPaused) ?? [];
};
